// NOTE(jj): These types are for the React components to consume.
//
// Yes, they are very similar in shape to the monolith schema types:
// @app/types/monolith/cart, @app/types/monolith/types
//
// However, it is a deliberate choice to have a distinct type for React components,
// because this allows us to separate the concern of types returned form
// an external API, and the types used for rendering the app.
//
// This separation allows us to modify and alter types from the api,
// without needing to touch all components which consume the api.

export const couponTypes = {
  amount: 'AmountCoupon',
  freeSample: 'FreeSampleCoupon',
  freeShipping: 'FreeShippingCoupon',
  giftCertificate: 'GiftCertificateCoupon',
  percentOff: 'PercentOffCoupon',
  variablePercentOff: 'VariablePercentOffCoupon',
} as const;

export type CouponType = (typeof couponTypes)[keyof typeof couponTypes];

export type AssignedCoupon = {
  type: CouponType;
  code: string;
  display_name: string;
  expiry_date: string;
};

export type AppliedCoupon = {
  amount: string;
  type: CouponType;
  code: string;
  display_name: string;
  expiry_date: string;
};

export type ShippingAddress = {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  countryCode?: string | null;
  postcode?: string | null;
  state?: string | null;
};

// XXX(jj): This type is not very good, as every property is optional, making it extremely akward to use.
// This causes some pretty ugly code downstream.
//
// This was caused, due to it needing to be compatible with goth GQL and Monoloth error types.
// Lets get over the hump of removeing GQL, and then we can refine this type to be more usful later.
export type CouponError = {
  // XXX(jj) 20260228: After we remove cart-bfff/utils, lets change error_code to be just a "string"
  //
  // string|number is required because fromGqlCouponError
  // receives CartUserError.code which is typed as string | number.
  // (app/pages/cart/components/Coupons/CouponsGqlContainer.tsx:150)
  //
  // We can see that line item errors are leaking into this coupon related type:
  // string: app/types/cart-bff/utils.ts:69,84 - coupon errors from Monolith API (e.g., 'invalid', 'expired')
  // number: app/types/cart-bff/utils.ts:137,146,153 - line item errors using internal constants (1000, 1100, 1200)
  error_code?: string | number;
  field?: string[];
  error_params?: Record<string, string>;
  message?: string;
};

export type CouponResponseError = {
  status?: number;
  message?: string;
};

// XXX(jj): these Handler types are centralized here
// because they're prop-drilled across multiple
// component levels, making co-location impractical for type consistency.
type ApplyCouponInput = {
  couponCode: string;
  shippingAddress?: ShippingAddress;
};

type RemoveCouponInput = {
  shippingAddress?: ShippingAddress;
};

type CouponHandlerOptions<T> = {
  input: T;
  signal?: AbortSignal;
  onCompleted?: (result: { errors?: CouponError[] }) => void;
  onError?: (result: { responseError?: CouponResponseError }) => void;
};

export type ApplyCouponHandlerOptions = CouponHandlerOptions<ApplyCouponInput>;
export type RemoveCouponHandlerOptions =
  CouponHandlerOptions<RemoveCouponInput>;

type CouponHandler<T> = (options: T) => Promise<void>;

export type ApplyCouponHandler = CouponHandler<ApplyCouponHandlerOptions>;
export type RemoveCouponHandler = CouponHandler<RemoveCouponHandlerOptions>;

type UseCouponHandlerResult<T> = {
  handler: T;
  isLoading: boolean;
};

export type ApplyCouponHandlerResult =
  UseCouponHandlerResult<ApplyCouponHandler>;

export type RemoveCouponHandlerResult =
  UseCouponHandlerResult<RemoveCouponHandler>;
